import { ExternalLink, X } from "lucide-react";
import { MarketTickerSparkline } from "./MarketTickerSparkline";
import type { MarketTickerQuote } from "./market-ticker-types";
import {
  formatPrice,
  formatSigned,
  formatSignedPct,
} from "./market-ticker-types";
import { FintheonNumericText } from "../../numbers/FintheonNumericText";

export function MarketTickerCard({
  quote,
  onClose,
  isClosing,
}: {
  quote: MarketTickerQuote;
  onClose?: () => void;
  isClosing?: boolean;
}) {
  const positive = quote.change >= 0;
  const tvUrl = `https://www.tradingview.com/chart/?symbol=${encodeURIComponent(quote.tvSymbol)}`;
  return (
    <div
      className={`fintheon-popover-surface fintheon-popover-motion fintheon-market-ticker-card w-[286px] p-3 ${
        isClosing ? "is-closing" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--fintheon-accent)]">
            {quote.label} · {quote.group}
          </p>
          <p className="mt-1 text-2xl font-semibold text-[var(--fintheon-text)]">
            <FintheonNumericText
              text={formatPrice(quote.price)}
              kind="instrument-price"
            />
          </p>
          <p
            className={
              positive ? "text-xs text-emerald-300" : "text-xs text-red-300"
            }
          >
            <FintheonNumericText
              text={formatSigned(quote.change)}
              kind="point-change"
            />{" "}
            (
            <FintheonNumericText
              text={formatSignedPct(quote.changePercent)}
              kind="percentage-change"
            />
            )
          </p>
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--fintheon-muted)]"
            title="Close ticker card"
          >
            <X size={13} />
          </button>
        ) : null}
      </div>

      <MarketTickerSparkline quote={quote} positive={positive} />

      <div className="mt-3 grid grid-cols-2 gap-2 text-[10px]">
        <Metric label="Open" value={formatPrice(quote.open)} />
        <Metric
          label="Day range"
          value={`${formatPrice(quote.low)}-${formatPrice(quote.high)}`}
        />
        <Metric
          label="5D high"
          value={nullablePrice(quote.rolling5dHigh ?? quote.rolling7dHigh)}
        />
        <Metric
          label="5D low"
          value={nullablePrice(quote.rolling5dLow ?? quote.rolling7dLow)}
        />
      </div>

      <div className="mt-3 flex items-center justify-end gap-2">
        <a
          href={tvUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] text-[var(--fintheon-accent)]"
        >
          TV <ExternalLink size={11} />
        </a>
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-[#3a2f13] bg-[#11100b] p-2">
      <p className="text-[var(--fintheon-muted)]">{label}</p>
      <p className="mt-1 font-mono text-[var(--fintheon-text)]">
        <FintheonNumericText text={value} />
      </p>
    </div>
  );
}

function nullablePrice(value: number | null | undefined): string {
  return typeof value === "number" && Number.isFinite(value)
    ? formatPrice(value)
    : "N/A";
}
